import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Flag, RefreshCcw, Eye, EyeOff, Check, X } from "lucide-react";
import { Modal, FieldTextarea } from "./AdminBusinesses";

const STATUS_LABEL = {
  pending:   { tone: "bg-amber-100 text-amber-800 border-amber-200", label: "Pending" },
  open:      { tone: "bg-amber-100 text-amber-800 border-amber-200", label: "Pending" },
  resolved:  { tone: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Resolved" },
  dismissed: { tone: "bg-slate-200 text-slate-700 border-slate-300",    label: "Dismissed" },
};

/**
 * AdminReports: reported-reviews & abuse moderation center.
 * Sources: `business_review_reports` collection.
 * Admin actions: dismiss / hide_review / resolve. Every action is audit logged.
 */
export default function AdminReports() {
  const [status, setStatus] = useState("pending");
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [acting, setActing]   = useState(null); // {report, action}

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    params.set("limit", "200");
    setLoading(true);
    api.get(`/admin/reports/reviews?${params}`)
      .then((r) => setItems(r.data.items || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const performAction = ({ note }) => {
    const { report, action } = acting;
    api.post(`/admin/reports/reviews/${report.id}/resolve`, { action, note })
      .then(() => {
        toast.success(`Action recorded: ${action}.`);
        setActing(null);
        load();
      })
      .catch((e) => toast.error(formatApiError(e)));
  };

  return (
    <div data-testid="admin-reports-page">
      <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
        <Flag className="w-5 h-5" /> Reports &amp; abuse
      </h1>
      <p className="text-sm text-slate-600 mt-1 max-w-3xl">
        Review reports submitted by the public against business reviews. Hiding a review
        sets <code>hidden=true</code> on the review and creates an audit-log entry. Dismissing
        a report closes it without changing the review.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {["pending", "resolved", "dismissed", ""].map((s) => (
          <button key={s || "all"} onClick={() => setStatus(s)}
                  data-testid={`reports-filter-${s || "all"}`}
                  className={`px-3 py-1.5 rounded-lg text-sm border transition ${
                    status === s ? "bg-[#1B4332] text-white border-[#1B4332]"
                                 : "bg-white border-slate-300 hover:bg-slate-50"}`}>
            {s || "All"}
          </button>
        ))}
        <button onClick={load}
                className="px-3 py-1.5 text-sm rounded-lg border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1">
          <RefreshCcw className="w-3.5 h-3.5" /> Refresh
        </button>
        <span className="text-xs text-slate-500 ml-auto">
          {loading ? "Loading…" : `${items.length} reports`}
        </span>
      </div>

      <div className="mt-4 space-y-3" data-testid="reports-list">
        {items.map((r) => {
          const tone = STATUS_LABEL[r.status || "pending"] || STATUS_LABEL.pending;
          const review = r.review || {};
          return (
            <article key={r.id} data-testid={`report-row-${r.id}`}
                       className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-slate-900">
                    {r.company_name || `Business ${r.company_id?.slice(0, 8) || "-"}`}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    Reported {(r.created_at || "").slice(0, 19).replace("T", " ")}
                    {r.reporter_ip ? ` · IP ${r.reporter_ip}` : ""}
                  </div>
                </div>
                <span className={`px-2 py-0.5 rounded-full border text-[11px] font-semibold ${tone.tone}`}>
                  {tone.label}
                </span>
              </div>
              <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-800">
                <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">
                  Review snippet
                </div>
                <div className="mt-1">
                  {review.text ? `"${review.text}"` : <em className="text-slate-500">Review text unavailable.</em>}
                </div>
                <div className="text-[11px] text-slate-500 mt-1">
                  {review.author_name ? `- ${review.author_name}` : ""} · rating {review.rating ?? "-"}/5
                  {review.hidden && <span className="ml-2 text-rose-700 font-semibold">[hidden]</span>}
                </div>
              </div>
              {r.reason && (
                <div className="mt-2 text-sm text-slate-700">
                  <span className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Reason</span>
                  <div className="mt-0.5">{r.reason}</div>
                </div>
              )}
              {(!r.status || ["pending", "open"].includes(r.status)) && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <button data-testid={`report-action-dismiss-${r.id}`}
                          onClick={() => setActing({ report: r, action: "dismiss" })}
                          className="px-3 py-1.5 text-xs rounded-lg border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1">
                    <X className="w-3 h-3" /> Dismiss
                  </button>
                  <button data-testid={`report-action-hide-${r.id}`}
                          onClick={() => setActing({ report: r, action: "hide_review" })}
                          disabled={review.hidden}
                          className="px-3 py-1.5 text-xs rounded-lg border border-rose-300 text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1 disabled:opacity-50">
                    {review.hidden ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />} Hide review
                  </button>
                  <button data-testid={`report-action-resolve-${r.id}`}
                          onClick={() => setActing({ report: r, action: "resolve" })}
                          className="px-3 py-1.5 text-xs rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-50 inline-flex items-center gap-1">
                    <Check className="w-3 h-3" /> Mark resolved
                  </button>
                </div>
              )}
              {r.resolution && (
                <div className="mt-2 text-xs text-slate-500">
                  Last action: <b>{r.resolution}</b>{r.resolution_note ? `: ${r.resolution_note}` : ""}
                </div>
              )}
            </article>
          );
        })}
        {!loading && items.length === 0 && (
          <div className="text-center text-slate-500 text-sm py-12 rounded-2xl border border-dashed border-slate-200">
            No reports match.
          </div>
        )}
      </div>

      {acting && <ResolveModal acting={acting} onCancel={() => setActing(null)} onSave={performAction} />}
    </div>
  );
}

function ResolveModal({ acting, onCancel, onSave }) {
  const [note, setNote] = useState("");
  const labels = {
    dismiss:     "Dismiss report",
    hide_review: "Hide review",
    resolve:     "Mark report resolved",
  };
  return (
    <Modal title={labels[acting.action]} onClose={onCancel} testId="report-resolve-modal">
      <p className="text-sm text-slate-700">
        {acting.action === "dismiss"
          ? "Close this report without touching the review. The reporter is not notified."
          : acting.action === "hide_review"
          ? "Hides the review from the public business profile. The review is kept (not deleted)."
          : "Marks this report as handled. The review is left as-is."}
      </p>
      <div className="mt-3">
        <FieldTextarea label="Internal note (optional)" value={note} onChange={setNote} />
      </div>
      <div className="mt-4 flex justify-end gap-2">
        <button onClick={onCancel} className="px-3 py-1.5 text-sm rounded border border-slate-300">Cancel</button>
        <button data-testid="report-resolve-confirm" onClick={() => onSave({ note })}
                className="px-3 py-1.5 text-sm rounded bg-[#1B4332] text-white">Confirm</button>
      </div>
    </Modal>
  );
}
